import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { getViewers, setViewers } from '../api/client';

/**
 * Share action: teammates only (docs/viewer-sharing.md). Add a colleague by
 * email as VIEW-ONLY. They sign in as themselves, see the full proposal
 * including internal detail, and can change nothing. Never gated on
 * readiness — a half-built proposal is exactly when a teammate needs to look.
 * There is deliberately no public client link here.
 */
export function ShareMenu({ proposalId }: { proposalId: string }): ReactElement {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  const [viewers, setViewerList] = useState<string[] | null>(null);
  const [viewerDraft, setViewerDraft] = useState('');
  const [viewerBusy, setViewerBusy] = useState(false);
  const [viewerError, setViewerError] = useState<string | null>(null);

  const saveViewers = (next: string[]) => {
    setViewerBusy(true);
    setViewerError(null);
    void setViewers(proposalId, next)
      .then((res) => {
        setViewerList(res.viewers);
        setViewerDraft('');
      })
      .catch((cause: unknown) => setViewerError(cause instanceof Error ? cause.message : String(cause)))
      .finally(() => setViewerBusy(false));
  };

  const addViewer = () => {
    const email = viewerDraft.trim();
    if (!email || !viewers) return;
    saveViewers([...viewers, email]);
  };

  const openMenu = () => {
    setOpen(true);
    if (viewers === null) {
      void getViewers(proposalId)
        .then((res) => setViewerList(res.viewers))
        .catch((cause: unknown) => setViewerError(cause instanceof Error ? cause.message : String(cause)));
    }
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="menu" ref={root}>
      <button
        type="button"
        className="workspace-share-button"
        onClick={() => (open ? setOpen(false) : openMenu())}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Share proposal"
        title="Share proposal"
      >
        Share
      </button>
      {open ? (
        <div className="menu-panel share-panel" role="menu">
          <div className="share-section-label">Teammates</div>
          <p className="modal-help">
            View only. They sign in as themselves and see everything you see, but can't edit or chat.
          </p>
          <form
            className="share-row"
            onSubmit={(event) => {
              event.preventDefault();
              addViewer();
            }}
          >
            <input
              className="share-input"
              type="email"
              placeholder="name@visitiq.io"
              value={viewerDraft}
              disabled={viewers === null || viewerBusy}
              onChange={(event) => setViewerDraft(event.target.value)}
              aria-label="Teammate email"
            />
            <button type="submit" className="btn-primary" disabled={!viewerDraft.trim() || viewers === null || viewerBusy}>
              Add
            </button>
          </form>
          {viewerError ? <div className="menu-empty">{viewerError}</div> : null}
          {viewers && viewers.length > 0 ? (
            <ul className="share-viewers">
              {viewers.map((viewer) => (
                <li key={viewer} className="share-viewer">
                  <span className="share-viewer-name">{viewer}</span>
                  <button
                    type="button"
                    className="btn btn-quiet share-viewer-remove"
                    disabled={viewerBusy}
                    onClick={() => saveViewers(viewers.filter((item) => item !== viewer))}
                    aria-label={`Remove ${viewer}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
